import React from 'react';
import { Stand } from '../types/stand.types';

interface DuplicateEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReplace: () => void;
  onAdd: () => void;
  onNewQuery: () => void;
  previousStands: Stand[];
  newStands: Stand[];
  previousOrganization: string;
  newOrganization: string;
  userEmail: string;
}

const DuplicateEmailModal: React.FC<DuplicateEmailModalProps> = ({
  isOpen,
  onClose,
  onReplace,
  onAdd,
  onNewQuery,
  previousStands,
  newStands,
  previousOrganization,
  newOrganization,
  userEmail
}) => {
  if (!isOpen) return null;

  const formatStandsList = (stands: Stand[]) => {
    return stands.map(stand => stand.name).join(', ');
  };

  const getTotalPrice = (stands: Stand[]) => {
    return stands.reduce((total, stand) => total + (stand.price || 0), 0);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px'
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '20px',
            fontWeight: '600',
            color: '#1f2937',
            margin: '0 0 8px 0'
          }}>
            Consulta Duplicada Detectada
          </h2>
          <p style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '14px',
            color: '#6b7280',
            margin: 0
          }}>
            Ya existe una consulta previa para <strong>{userEmail}</strong>
          </p>
        </div>

        {/* Previous Query */}
        <div style={{
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <h3 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '16px',
            fontWeight: '600',
            color: '#374151',
            margin: '0 0 8px 0'
          }}>
            Consulta Anterior
          </h3>
          <p style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 8px 0'
          }}>
            <strong>Organización:</strong> {previousOrganization}
          </p>
          <p style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 8px 0'
          }}>
            <strong>Stands:</strong> {formatStandsList(previousStands)}
          </p>
          <p style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '14px',
            color: '#6b7280',
            margin: 0
          }}>
            <strong>Total:</strong> ${getTotalPrice(previousStands).toLocaleString()}
          </p>
        </div>

        {/* New Query */}
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <h3 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '16px',
            fontWeight: '600',
            color: '#374151',
            margin: '0 0 8px 0'
          }}>
            Nueva Consulta
          </h3>
          <p style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 8px 0'
          }}>
            <strong>Organización:</strong> {newOrganization}
          </p>
          <p style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 8px 0'
          }}>
            <strong>Stands:</strong> {formatStandsList(newStands)}
          </p>
          <p style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '14px',
            color: '#6b7280',
            margin: 0
          }}>
            <strong>Total:</strong> ${getTotalPrice(newStands).toLocaleString()}
          </p>
        </div>

        {/* Action Buttons */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '16px',
            fontWeight: '600',
            color: '#374151',
            margin: '0 0 12px 0'
          }}>
            ¿Qué deseas hacer?
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Replace Button */}
            <button
              onClick={onReplace}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: '#dc2626',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#b91c1c';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#dc2626';
              }}
            >
              🔄 Reemplazar consulta anterior
            </button>

            {/* Add Button */}
            <button
              onClick={onAdd}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: '#059669',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#047857';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#059669';
              }}
            >
              ➕ Sumar a consulta anterior
            </button>

            {/* New Query Button */}
            <button
              onClick={onNewQuery}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: '#2563eb',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1d4ed8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb';
              }}
            >
              🆕 Crear nueva consulta
            </button>
          </div>
        </div>

        {/* Cancel Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: 'transparent',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#9ca3af';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateEmailModal;
